import { useEffect, useRef, useState } from 'react';
import { X, BookOpen, Upload, Calendar, Clock, CheckCircle2, AlertCircle, FileSpreadsheet, Layers } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useDialogA11y } from '../ui/useDialogA11y';
import { Spinner } from '../ui/Spinner';
import { toast } from '../ui/Toaster';
import { listTemplates, getTemplateBundle, createCourseFromTemplate } from '../../lib/templates';
import { parseCalendarExcel } from '../../lib/excel-calendar';
import type { CourseTemplate, TemplateBundle, ParsedCalendar } from '../../types/templates';
import { fmtDateLong } from '../../lib/datetime';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (newCourseId: string) => void;
}

/**
 * Modal "Crear curso desde plantilla".
 *
 * Flujo:
 *   1) El admin selecciona una plantilla del catálogo (precargado en Supabase)
 *   2) Llena datos básicos del curso (nombre, descripción, docente)
 *   3) Sube un Excel con el calendario de fechas (formato como "Diplomado 010")
 *   4) Ajusta horario por defecto y link Zoom
 *   5) Ve preview y crea el curso
 *
 * No reemplaza al CourseModal viejo — vive al lado.
 */
export function CreateCourseFromTemplateModal({ isOpen, onClose, onSuccess }: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogA11y(isOpen, onClose, dialogRef);

  // Catálogo
  const [templates, setTemplates] = useState<CourseTemplate[]>([]);
  const [docentes,  setDocentes]  = useState<{ id: string; full_name: string | null; email: string | null }[]>([]);

  // Plantilla seleccionada (con sus topics + deliveries)
  const [selectedTplId, setSelectedTplId] = useState<string>('');
  const [bundle,        setBundle]        = useState<TemplateBundle | null>(null);
  const [loadingBundle, setLoadingBundle] = useState(false);

  // Datos del curso
  const [name,           setName]          = useState('');
  const [description,    setDescription]   = useState('');
  const [docenteId,      setDocenteId]     = useState('');
  const [secondaryDocId, setSecondaryDocId]= useState('');

  // Calendario subido
  const [excelFile,     setExcelFile]     = useState<File | null>(null);
  const [parsed,        setParsed]        = useState<ParsedCalendar | null>(null);
  const [parsing,       setParsing]       = useState(false);

  // Horario y zoom
  const [startTime,     setStartTime]     = useState('19:00');
  const [endTime,       setEndTime]       = useState('22:00');
  const [zoomLink,      setZoomLink]      = useState('');

  // Estado
  const [submitting,    setSubmitting]    = useState(false);
  const [submitError,   setSubmitError]   = useState<string | null>(null);

  // ─── Cargar catálogo al abrir ──────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    let cancel = false;
    (async () => {
      const [tpls, { data: docs }] = await Promise.all([
        listTemplates(),
        supabase.from('profiles').select('id, full_name, email').eq('role', 'docente').eq('active', true),
      ]);
      if (cancel) return;
      setTemplates(tpls);
      setDocentes((docs ?? []) as any[]);
      // Si solo hay 1 plantilla, preseleccionarla
      if (tpls.length === 1) setSelectedTplId(tpls[0].id);
    })();
    return () => { cancel = true; };
  }, [isOpen]);

  // ─── Cargar bundle de la plantilla cuando cambia la selección ─────
  useEffect(() => {
    if (!selectedTplId) { setBundle(null); return; }
    let cancel = false;
    (async () => {
      setLoadingBundle(true);
      const b = await getTemplateBundle(selectedTplId);
      if (cancel) return;
      setBundle(b);
      setLoadingBundle(false);
    })();
    return () => { cancel = true; };
  }, [selectedTplId]);

  // ─── Limpiar todo al cerrar ────────────────────────────────────────
  useEffect(() => {
    if (isOpen) return;
    // Reset cuando se cierra
    setSelectedTplId('');
    setBundle(null);
    setName(''); setDescription(''); setDocenteId(''); setSecondaryDocId('');
    setExcelFile(null); setParsed(null);
    setStartTime('19:00'); setEndTime('22:00'); setZoomLink('');
    setSubmitError(null);
  }, [isOpen]);

  // ─── Handler de subida de Excel ────────────────────────────────────
  const onPickExcel = async (file: File | null) => {
    setExcelFile(file);
    setParsed(null);
    if (!file) return;
    setParsing(true);
    try {
      const result = await parseCalendarExcel(file);
      setParsed(result);
    } catch (e: any) {
      setParsed({ classes: [], deliveries: [], errors: [e?.message ?? 'Error desconocido'] });
    } finally {
      setParsing(false);
    }
  };

  // ─── Validación pre-submit ────────────────────────────────────────
  const canSubmit = (): { ok: boolean; reason?: string } => {
    if (!selectedTplId)             return { ok: false, reason: 'Elegí una plantilla.' };
    if (!name.trim())               return { ok: false, reason: 'Falta el nombre del curso.' };
    if (!docenteId)                 return { ok: false, reason: 'Elegí el docente principal.' };
    if (!parsed)                    return { ok: false, reason: 'Subí el Excel con las fechas.' };
    if (parsed.classes.length === 0)return { ok: false, reason: 'El Excel no tiene clases válidas.' };
    if (!bundle)                    return { ok: false, reason: 'La plantilla aún se está cargando.' };
    // Verificar que las clases del Excel matcheen los temas de la plantilla
    const classNumbers = new Set(parsed.classes.map(c => c.class_number));
    const topicNumbers = new Set(bundle.topics.map(t => t.default_class_number));
    const sinTopic = [...classNumbers].filter(n => !topicNumbers.has(n!));
    if (sinTopic.length > 0) {
      return { ok: false, reason: `Hay clases en el Excel sin tema en la plantilla: ${sinTopic.join(', ')}` };
    }
    return { ok: true };
  };

  // ─── Submit ────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const v = canSubmit();
    if (!v.ok) { setSubmitError(v.reason ?? null); return; }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const courseId = await createCourseFromTemplate({
        template_id:        selectedTplId,
        name:               name.trim(),
        description:        description.trim() || null,
        docente_id:         docenteId,
        secondary_docente_id: secondaryDocId || null,
        calendar:           [...parsed!.classes, ...parsed!.deliveries],
        default_start_time: startTime,
        default_end_time:   endTime,
        default_zoom_link:  zoomLink.trim() || null,
      });
      if (!courseId) {
        setSubmitError('No se pudo crear el curso. Revisá la consola del navegador para más detalles.');
        return;
      }
      toast.success('¡Curso creado correctamente!');
      onSuccess?.(courseId);
      onClose();
    } catch (e: any) {
      setSubmitError(e?.message ?? 'Error inesperado.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const v = canSubmit();
  const totalClases = parsed?.classes.length ?? 0;
  const totalEntregas = parsed?.deliveries.length ?? 0;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title-create-from-tpl"
        tabIndex={-1}
        className="glass-surface rounded-xl max-w-4xl w-full border border-white/[0.06] max-h-[92vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/[0.06] flex-shrink-0">
          <div>
            <div className="text-[10px] tracking-[3px] uppercase font-semibold text-cubus-lime mb-1">
              <Layers className="inline w-3 h-3 mr-1" /> Crear desde plantilla
            </div>
            <h2 id="modal-title-create-from-tpl" className="text-xl font-bold text-white">
              Nuevo curso a partir de plantilla
            </h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/[0.04] rounded-lg transition-all" aria-label="Cerrar">
            <X className="w-5 h-5 text-muted" />
          </button>
        </div>

        {/* Body scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* ── 1. PLANTILLA ── */}
          <section>
            <SectionTitle icon={<BookOpen className="w-4 h-4" />} title="1. Plantilla" />
            <select
              value={selectedTplId}
              onChange={(e) => setSelectedTplId(e.target.value)}
              className="w-full bg-cubus-deep border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
            >
              <option value="">— elegí una plantilla —</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name} {t.total_clases ? `· ${t.total_clases} clases` : ''}
                </option>
              ))}
            </select>
            {loadingBundle && <p className="text-xs text-white/50 mt-2 inline-flex items-center gap-2"><Spinner size="sm" /> Cargando temas…</p>}
            {bundle && (
              <p className="text-xs text-white/50 mt-2">
                Esta plantilla tiene <b className="text-cubus-lime">{bundle.topics.length} temas</b> y{' '}
                <b className="text-cubus-lime">{bundle.deliveries.length} entregas</b> estándar.
              </p>
            )}
          </section>

          {/* ── 2. DATOS DEL CURSO ── */}
          <section>
            <SectionTitle icon={<BookOpen className="w-4 h-4" />} title="2. Datos del curso" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="text-xs text-white/60 mb-1 block">Nombre del curso *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ej. Diplomado BIM Revit Arquitectura 011 · Chile"
                  className="w-full bg-cubus-deep border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-white/60 mb-1 block">Descripción</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  className="w-full bg-cubus-deep border border-white/10 rounded-lg px-3 py-2 text-sm text-white resize-none"
                />
              </div>
              <div>
                <label className="text-xs text-white/60 mb-1 block">Docente principal *</label>
                <select
                  value={docenteId}
                  onChange={(e) => setDocenteId(e.target.value)}
                  className="w-full bg-cubus-deep border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                >
                  <option value="">— elegí docente —</option>
                  {docentes.map(d => (
                    <option key={d.id} value={d.id}>{d.full_name || d.email}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-white/60 mb-1 block">Docente secundario (opcional)</label>
                <select
                  value={secondaryDocId}
                  onChange={(e) => setSecondaryDocId(e.target.value)}
                  className="w-full bg-cubus-deep border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                >
                  <option value="">— ninguno —</option>
                  {docentes.filter(d => d.id !== docenteId).map(d => (
                    <option key={d.id} value={d.id}>{d.full_name || d.email}</option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          {/* ── 3. CALENDARIO (Excel) ── */}
          <section>
            <SectionTitle icon={<Calendar className="w-4 h-4" />} title="3. Calendario (Excel)" />
            <p className="text-xs text-white/50 mb-3">
              Subí un .xlsx con la hoja <b>"Fechas Clases"</b> que tenga las columnas: <b>MODULOS</b> (CLASE 1, CLASE 2…, ENTREGA 1, ENTREGA FINAL), <b>TEMAS</b> y <b>FECHA</b>.
            </p>
            <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cubus-lime/15 border border-cubus-lime/40 text-cubus-lime hover:bg-cubus-lime/25 transition-all">
              <FileSpreadsheet className="w-4 h-4" />
              <span>{excelFile ? 'Cambiar archivo' : 'Subir Excel'}</span>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => onPickExcel(e.target.files?.[0] ?? null)}
                className="hidden"
              />
            </label>
            {excelFile && (
              <span className="ml-3 text-xs text-white/70">{excelFile.name}</span>
            )}
            {parsing && (
              <div className="mt-3 text-xs text-white/60 inline-flex items-center gap-2">
                <Spinner size="sm" /> Procesando archivo…
              </div>
            )}
            {parsed && (
              <div className="mt-3 space-y-2">
                <div className="flex flex-wrap gap-3 text-xs">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-cubus-lime/15 text-cubus-lime border border-cubus-lime/30">
                    <CheckCircle2 className="w-3 h-3" /> {totalClases} clases
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-cubus-cyan/15 text-cubus-cyan border border-cubus-cyan/30">
                    <CheckCircle2 className="w-3 h-3" /> {totalEntregas} entregas
                  </span>
                  {parsed.errors.length > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-rose-500/15 text-rose-400 border border-rose-500/30">
                      <AlertCircle className="w-3 h-3" /> {parsed.errors.length} aviso{parsed.errors.length === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
                {parsed.errors.length > 0 && (
                  <details className="text-xs text-rose-400/80">
                    <summary className="cursor-pointer">Ver avisos</summary>
                    <ul className="mt-1 ml-4 list-disc space-y-0.5">
                      {parsed.errors.map((er, i) => <li key={i}>{er}</li>)}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </section>

          {/* ── 4. HORARIO + ZOOM ── */}
          <section>
            <SectionTitle icon={<Clock className="w-4 h-4" />} title="4. Horario por defecto y Zoom" />
            <p className="text-xs text-white/50 mb-3">
              Estos valores se aplican a todas las clases. Vas a poder editarlos clase por clase después si hace falta.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-white/60 mb-1 block">Hora inicio</label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="w-full bg-cubus-deep border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                />
              </div>
              <div>
                <label className="text-xs text-white/60 mb-1 block">Hora fin</label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="w-full bg-cubus-deep border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                />
              </div>
              <div>
                <label className="text-xs text-white/60 mb-1 block">Link Zoom (opcional)</label>
                <input
                  type="url"
                  value={zoomLink}
                  onChange={(e) => setZoomLink(e.target.value)}
                  placeholder="https://zoom.us/j/..."
                  className="w-full bg-cubus-deep border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                />
              </div>
            </div>
          </section>

          {/* ── 5. PREVIEW ── */}
          {parsed && parsed.classes.length > 0 && bundle && (
            <section>
              <SectionTitle icon={<CheckCircle2 className="w-4 h-4" />} title="5. Preview del calendario" />
              <div className="max-h-72 overflow-y-auto scroll-cubus border border-white/[0.06] rounded-lg">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-cubus-deep">
                    <tr className="border-b border-white/10 text-white/40 uppercase tracking-wider">
                      <th className="text-left py-2 px-3">Clase</th>
                      <th className="text-left py-2 px-3">Tema</th>
                      <th className="text-left py-2 px-3">Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Clases con el TEMA del catálogo */}
                    {parsed.classes.map((c) => {
                      const topic = bundle.topics.find(t => t.default_class_number === c.class_number);
                      return (
                        <tr key={`c-${c.class_number}`} className="border-b border-white/[0.04] text-white/80">
                          <td className="py-2 px-3 text-cubus-lime font-medium">CLASE {c.class_number}</td>
                          <td className="py-2 px-3">{topic?.name ?? <span className="text-rose-400">(sin tema)</span>}</td>
                          <td className="py-2 px-3 text-white/60">{fmtDateLong(c.date)}</td>
                        </tr>
                      );
                    })}
                    {/* Entregas */}
                    {parsed.deliveries.map((d) => {
                      const tpl = bundle.deliveries.find(td => td.delivery_number === d.delivery_number);
                      return (
                        <tr key={`d-${d.delivery_number}`} className="border-b border-white/[0.04] text-white/80 bg-cubus-cyan/5">
                          <td className="py-2 px-3 text-cubus-cyan font-medium">
                            {d.delivery_number === 99 ? 'ENTREGA FINAL' : `ENTREGA ${d.delivery_number}`}
                          </td>
                          <td className="py-2 px-3">{tpl?.name ?? d.display_label}</td>
                          <td className="py-2 px-3 text-white/60">{fmtDateLong(d.date)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {submitError && (
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-300 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{submitError}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-white/[0.06] flex-shrink-0">
          <span className="text-xs text-white/40">
            {v.ok ? '✓ Todo listo para crear el curso' : v.reason}
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-white/70 hover:bg-white/[0.05] transition-all text-sm"
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={!v.ok || submitting}
              className="px-4 py-2 rounded-lg bg-cubus-lime text-cubus-bg font-medium hover:bg-cubus-lime/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all inline-flex items-center gap-2 text-sm"
            >
              {submitting ? <Spinner size="sm" /> : <Upload className="w-4 h-4" />}
              {submitting ? 'Creando…' : 'Crear curso'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-white">
      <span className="text-cubus-lime">{icon}</span>
      {title}
    </div>
  );
}
